import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  Renderer2,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { filter, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MenuTool } from '../../interfaces/MenuTool.interface';
import { NavigationItem } from '../../interfaces/navigationList.interface';
import {
  HomeButtonConfig,
  Profile,
} from '../../interfaces/PageContainerConfiguration.interface';
import { PageContainerService } from '../../services/page-container.service';
import { AppsToolsMenuComponent } from '../apps-tools-menu/apps-tools-menu.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { LOG_IN_ROUTE } from 'src/app/shared/common/constants';
import { ProfileService } from 'src/app/core/services/profile.service';
import { MultiSettingsMenuComponent } from '../multi-settings-menu/multi-settings-menu.component';
import { AuthService } from '@core/services';

@Component({
  selector: 'z-side-menu-bar',
  standalone: true,
  imports: [
    CommonModule,
    IconButtonComponent,
    AppsToolsMenuComponent,
    RouterModule,
    MenuModule,
    MultiSettingsMenuComponent,
  ],
  templateUrl: './side-menu-bar.component.html',
  styleUrls: ['./side-menu-bar.component.scss'],
})
export class SideMenuBarComponent {
  readonly pageContainerService = inject(PageContainerService);
  readonly router = inject(Router);
  private readonly _renderer = inject(Renderer2);
  private readonly _profileService = inject(ProfileService);
  private readonly _authService = inject(AuthService);

  navigationList = input.required<NavigationItem[]>();
  dashboardRoutes = input.required<NavigationItem[]>();
  settingsRoutes = input.required<NavigationItem>();
  homeButton = input.required<HomeButtonConfig>();
  tools = input.required<MenuTool[]>();
  profile = input.required<Profile>();
  settingURL = input<string>();
  hideTools = input<boolean>(false);
  profileMenuItems = signal<MenuItem[]>([]);

  isHomeButtonActive = signal(false);
  routerEvents$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    tap(event => {
      if (event.urlAfterRedirects === this.homeButton().routeUrl) {
        this.isHomeButtonActive.set(true);
      } else {
        this.isHomeButtonActive.set(false);
      }
    }),
  );

  public userName = computed<string>(() => {
    return this._authService.currentUser()?.fullName ?? 'Unknown User';
  });
  public userEmail = computed<string>(() => {
    return this._authService.currentUser()?.email ?? 'Unknown Email';
  });
  public userImage = computed<string>(
    () =>
      this._authService.currentUser()?.imageProfileDto?.imgDownloadUrl ??
      'assets/images/Avatars.png',
  );

  isExpandedMenu = signal<boolean>(false);

  isStandAlonePortal = signal<boolean>(environment.isStandalonePortal);

  ngAfterViewInit() {
    if (this.router.url === this.homeButton().routeUrl) {
      this.isHomeButtonActive.set(true);
    } else {
      this.isHomeButtonActive.set(false);
    }
    this.setUserDetailsFromLocalStorage();
  }

  expandMenu() {
    this.isExpandedMenu.update(x => !x);
    this.pageContainerService.menuExpanded.next(this.isExpandedMenu());
  }

  goToHome() {
    this.pageContainerService.activeSectionRoute.next(null);
  }

  navigateToProfile(url: string) {
    if (url?.length) this.router.navigate([url]);
  }

  navigateToMicroService(routeUrl: string) {
    const url = environment.localDomain + routeUrl;
    window.open(url, '_self');
  }

  private setUserDetailsFromLocalStorage(): void {
    const currentUser = this._authService.currentUser();
    if (currentUser) {
      this.profileMenuItems.set([
        {
          label: this.userName(),
          items: [
            {
              label: 'Profile Settings',
              icon: 'ph ph-user-circle',
              shortcut: '⌘+E',
              disabled: false,
              command: () => this.gotoProfile(),
            },
            {
              label: 'About',
              icon: 'ph ph-info',
              shortcut: '⌘+a',
              disabled: false,
              command: () => {
                this._profileService.showAboutDialog.set(true);
              },
            },
            {
              label: 'Log Out',
              icon: 'ph ph-sign-out',
              shortcut: '⌘+d',
              disabled: false,
              command: () => this.logOut(),
            },
          ],
        },
      ]);
    }
  }

  gotoProfile() {
    const url = environment.localDomain + this.profile().profileRoute;
    window.open(url, '_self');
  }

  logOut() {
    localStorage.clear();
    if (environment.isStandalonePortal) {
      this.router.navigate([LOG_IN_ROUTE]);
      return;
    }
    const url = environment.localDomain + LOG_IN_ROUTE;
    window.open(url, '_self');
  }
}
